/**
 * Schema-Derived Defaults
 *
 * Builds BehaviorData objects from schema field defaults.
 * Single source of truth for default values (derived from @default JSDoc tags).
 */

import type { BehaviorData } from '../behaviordata';
import type { FieldSchema } from './types';
import { getBehaviorSchema } from './index';

/**
 * Collect default values from a list of field schemas.
 * Recurses into nested object fields.
 */
function collectFieldDefaults(fields: FieldSchema[]): Record<string, unknown> {
    const defaults: Record<string, unknown> = {};

    for (const field of fields) {
        if (field.default !== undefined) {
            defaults[field.key] = structuredClone(field.default);
        } else if (field.type === 'object' && field.isMap) {
            // Map fields (Record<K,V>): `fields` describe the *value* shape, not
            // literal keys. Recursing would produce phantom entries named after
            // the value type's field names (e.g. a Keyframes map would default
            // to entries called "transition" and "entries"). Default to an
            // empty map so required fields aren't undefined.
            if (!field.optional) {
                defaults[field.key] = {};
            }
        } else if (field.type === 'object') {
            // Recurse into nested struct fields.
            const nested = collectFieldDefaults(field.fields);
            if (Object.keys(nested).length > 0) {
                defaults[field.key] = nested;
            }
        }
    }

    return defaults;
}

/**
 * Build a BehaviorData object from schema defaults, optionally merged with overrides.
 *
 * @param type - The behavior @type string (e.g., "Frame", "Animation", "Text")
 * @param overrides - Optional partial data to merge on top of defaults
 * @returns A BehaviorData object with @type + schema defaults + overrides
 *
 * @example
 * ```typescript
 * const data = createDefaultData<FrameData>("Frame");
 * // { "@type": "Frame", borderWidth: 3, color: "#FFD700" }
 *
 * const data = createDefaultData<AnimationData>("Animation", { clip: "Idle" });
 * // { "@type": "Animation", wrapMode: "Default", speed: 1, playAutomatically: true, clip: "Idle" }
 * ```
 */
export function createDefaultData<T extends BehaviorData>(
    type: string,
    overrides?: Partial<Record<string, unknown>> | BehaviorData
): T {
    const schema = getBehaviorSchema(type);

    const result: Record<string, unknown> = { "@type": type };

    if (schema) {
        const defaults = collectFieldDefaults(schema.fields);
        Object.assign(result, defaults);
    }

    if (overrides) {
        for (const [key, value] of Object.entries(overrides)) {
            if (value !== undefined) {
                result[key] = value;
            }
        }
    }

    return result as T;
}
